import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  BarChart3, TrendingUp, TrendingDown, Users, GraduationCap,
  Banknote, Wallet, Award, RefreshCw, Loader2, CheckCircle2, XCircle, ArrowRight,
  Download, FileSpreadsheet,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { useAuth, API, formatApiErrorDetail } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

const MONTHS_FR = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
const MONTHS_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function prevMonth(y, m) { return m === 1 ? [y - 1, 12] : [y, m - 1]; }

export default function AdminKPIs() {
  const { t, i18n } = useTranslation();
  const fmt = useFmtXof();
  const isEn = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en");
  const MONTHS = isEn ? MONTHS_EN : MONTHS_FR;

  const { user, loading } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [topEdu, setTopEdu] = useState([]);
  const [topPar, setTopPar] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalStatus, setWithdrawalStatus] = useState("PENDING");
  const [busy, setBusy] = useState(false);
  const [decisionNote, setDecisionNote] = useState({}); // wid -> text

  const monthOptions = useMemo(() => {
    const opts = [];
    let [y, m] = [today.getFullYear(), today.getMonth() + 1];
    for (let i = 0; i < 12; i++) {
      opts.push({ y, m, label: `${MONTHS[m - 1]} ${y}` });
      [y, m] = prevMonth(y, m);
    }
    return opts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MONTHS]);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [s, ts, te, tp, ws] = await Promise.all([
        axios.get(`${API}/admin/kpis/summary?year=${year}&month=${month}`),
        axios.get(`${API}/admin/kpis/timeseries?months=6`),
        axios.get(`${API}/admin/kpis/top-educators?year=${year}&month=${month}&limit=10`),
        axios.get(`${API}/admin/kpis/top-parents?year=${year}&month=${month}&limit=10`),
        axios.get(`${API}/admin/withdrawals?status=${withdrawalStatus}`),
      ]);
      setSummary(s.data);
      setSeries(ts.data.items || []);
      setTopEdu(te.data.items || []);
      setTopPar(tp.data.items || []);
      setWithdrawals(ws.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user?.role === "admin") loadAll(); /* eslint-disable-next-line */ }, [year, month, withdrawalStatus, user?.role]);

  const decide = async (wid, status) => {
    try {
      await axios.patch(`${API}/admin/withdrawals/${wid}`, {
        status,
        admin_note: (decisionNote[wid] || "").trim(),
      });
      toast.success(t("adminKpi.withdrawalUpdated"));
      setDecisionNote((s) => ({ ...s, [wid]: "" }));
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const res = await axios.get(`${API}${path}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("adminKpi.csvDownloaded"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  const periodSuffix = `${year}-${String(month).padStart(2, "0")}`;

  if (loading || user === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-[#16A34A]">{t("common.loading")}</div>;
  }
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="admin-kpis-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="rounded-3xl bg-white border border-[#E5EBE7] p-8 sm:p-10 relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/12 h-64 w-64 -top-20 -right-20" aria-hidden />
          <div className="relative">
            <span className="pill"><BarChart3 className="h-3.5 w-3.5" /> {t("adminKpi.pill")}</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">{t("adminKpi.title")}</h1>
            <p className="mt-3 text-[#4B5563] max-w-2xl">{t("adminKpi.intro")}</p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <select
                data-testid="kpi-period-select"
                value={`${year}-${month}`}
                onChange={(e) => { const [y, m] = e.target.value.split("-").map(Number); setYear(y); setMonth(m); }}
                className="rounded-full border border-[#E5EBE7] bg-white px-4 py-2 text-sm text-[#0A2518] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 capitalize"
              >
                {monthOptions.map((o) => (<option key={`${o.y}-${o.m}`} value={`${o.y}-${o.m}`} className="capitalize">{o.label}</option>))}
              </select>
              <Button onClick={loadAll} disabled={busy} variant="outline" className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]" data-testid="kpi-refresh-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {t("adminKpi.refresh")}
              </Button>
            </div>

            {/* Exports row */}
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#E5EBE7] pt-4" data-testid="kpi-exports-row">
              <span className="text-xs uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5 mr-2">
                <FileSpreadsheet className="h-3.5 w-3.5 text-[#16A34A]" /> {t("adminKpi.exportsTitle")}
              </span>
              <Button
                onClick={() => downloadCsv(`/admin/exports/bookings.csv?year=${year}&month=${month}`, `monrepetiteur_reservations_${periodSuffix}.csv`)}
                variant="outline" size="sm"
                className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
                data-testid="export-bookings-btn"
              >
                <Download className="h-4 w-4 mr-1.5" /> {t("adminKpi.exportBookings")}
              </Button>
              <Button
                onClick={() => downloadCsv(`/admin/exports/top-educators.csv?year=${year}&month=${month}&limit=200`, `monrepetiteur_top-educateurs_${periodSuffix}.csv`)}
                variant="outline" size="sm"
                className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
                data-testid="export-educators-btn"
              >
                <Download className="h-4 w-4 mr-1.5" /> {t("adminKpi.exportEducators")}
              </Button>
              <Button
                onClick={() => downloadCsv(`/admin/exports/top-parents.csv?year=${year}&month=${month}&limit=200`, `monrepetiteur_top-familles_${periodSuffix}.csv`)}
                variant="outline" size="sm"
                className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
                data-testid="export-parents-btn"
              >
                <Download className="h-4 w-4 mr-1.5" /> {t("adminKpi.exportParents")}
              </Button>
              <Button
                onClick={() => downloadCsv(`/admin/exports/withdrawals.csv?status=${withdrawalStatus}`, `monrepetiteur_retraits_${withdrawalStatus.toLowerCase()}.csv`)}
                variant="outline" size="sm"
                className="rounded-full border-[#16A34A] text-[#16A34A] hover:bg-[#F0FDF4]"
                data-testid="export-withdrawals-btn"
              >
                <Download className="h-4 w-4 mr-1.5" /> {t("adminKpi.exportWithdrawals")}
              </Button>
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          <KpiCard
            label={t("adminKpi.gross")}
            value={fmt(summary?.current?.gross ?? 0)}
            delta={summary?.deltas_pct?.gross}
            icon={Banknote}
            testid="kpi-gross"
          />
          <KpiCard
            label={t("adminKpi.fee")}
            value={fmt(summary?.current?.fee ?? 0)}
            delta={summary?.deltas_pct?.fee}
            icon={Award}
            accent
            testid="kpi-fee"
          />
          <KpiCard
            label={t("adminKpi.payout")}
            value={fmt(summary?.current?.payout ?? 0)}
            delta={summary?.deltas_pct?.payout}
            icon={Wallet}
            testid="kpi-payout"
          />
          <KpiCard
            label={t("adminKpi.bookings")}
            value={summary?.current?.bookings ?? 0}
            sub={t("adminKpi.hoursPaid", { hours: (summary?.current?.hours || 0).toFixed(1) })}
            delta={summary?.deltas_pct?.bookings}
            icon={BarChart3}
            testid="kpi-bookings"
          />
        </div>

        {/* Operational counts */}
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <OpCard icon={Users} label={t("adminKpi.totalParents")} value={summary?.operational?.total_parents ?? 0} sub={`+${summary?.operational?.new_parents ?? 0} ${t("adminKpi.thisMonth")}`} testid="kpi-total-parents" />
          <OpCard icon={GraduationCap} label={t("adminKpi.totalEducators")} value={summary?.operational?.total_educators ?? 0} sub={`+${summary?.operational?.new_educators ?? 0} ${t("adminKpi.thisMonth")}`} testid="kpi-total-educators" />
          <OpCard icon={CheckCircle2} label={t("adminKpi.confirmedSessions")} value={summary?.operational?.sessions_confirmed ?? 0} sub={summary?.period_label || ""} testid="kpi-sessions" />
          <OpCard icon={Wallet} label={t("adminKpi.pendingWithdrawals")} value={fmt(summary?.operational?.pending_withdrawals?.total ?? 0)} sub={`${summary?.operational?.pending_withdrawals?.count ?? 0} ${t("adminKpi.requests")}`} tone="warn" testid="kpi-pending-withdrawals" />
        </div>

        {/* Chart */}
        <Card className="mt-8 p-6 border-[#E5EBE7]" data-testid="kpi-chart-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h2 className="font-display text-xl font-semibold text-[#0A2518] flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#16A34A]" /> {t("adminKpi.timeseriesTitle")}
            </h2>
            <span className="text-xs text-[#6B7280]">{t("adminKpi.last6Months")}</span>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5EBE7" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6B7280" }} />
                <YAxis tickFormatter={(v) => `${Math.round(v / 1000)}k`} tick={{ fontSize: 12, fill: "#6B7280" }} />
                <Tooltip
                  formatter={(v) => fmt(v)}
                  labelStyle={{ color: "#0A2518", textTransform: "capitalize" }}
                  contentStyle={{ borderRadius: 12, border: "1px solid #E5EBE7" }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="payout" stackId="a" name={t("adminKpi.payout")} fill="#16A34A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="fee" stackId="a" name={t("adminKpi.fee")} fill="#0A422D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Top tables */}
        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          <Card className="p-6 border-[#E5EBE7]" data-testid="top-educators-card">
            <h3 className="font-display text-lg font-semibold text-[#0A2518] mb-3 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[#16A34A]" /> {t("adminKpi.topEducators")}
            </h3>
            <Table
              empty={t("adminKpi.empty")}
              rows={topEdu}
              cols={[
                { key: "name", label: t("adminKpi.thName"), render: (r) => (
                  <div>
                    <div className="text-[#0A2518] font-medium">{r.name}</div>
                    <div className="text-[11px] text-[#6B7280]">{r.city}</div>
                  </div>
                )},
                { key: "bookings", label: t("adminKpi.thBookings"), align: "right" },
                { key: "hours", label: t("adminKpi.thHours"), align: "right", render: (r) => `${r.hours.toFixed(1)} h` },
                { key: "gross", label: t("adminKpi.thGross"), align: "right", render: (r) => fmt(r.gross) },
              ]}
              testid="top-educators-table"
            />
          </Card>

          <Card className="p-6 border-[#E5EBE7]" data-testid="top-parents-card">
            <h3 className="font-display text-lg font-semibold text-[#0A2518] mb-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-[#16A34A]" /> {t("adminKpi.topParents")}
            </h3>
            <Table
              empty={t("adminKpi.empty")}
              rows={topPar}
              cols={[
                { key: "name", label: t("adminKpi.thName"), render: (r) => (
                  <div>
                    <div className="text-[#0A2518] font-medium">{r.name}</div>
                    <div className="text-[11px] text-[#6B7280]">{r.email}</div>
                  </div>
                )},
                { key: "bookings", label: t("adminKpi.thBookings"), align: "right" },
                { key: "hours", label: t("adminKpi.thHours"), align: "right", render: (r) => `${r.hours.toFixed(1)} h` },
                { key: "spend", label: t("adminKpi.thSpend"), align: "right", render: (r) => fmt(r.spend) },
              ]}
              testid="top-parents-table"
            />
          </Card>
        </div>

        {/* Withdrawals queue */}
        <Card className="mt-8 p-6 border-[#E5EBE7]" data-testid="withdrawals-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h3 className="font-display text-lg font-semibold text-[#0A2518] flex items-center gap-2">
              <Wallet className="h-5 w-5 text-[#16A34A]" /> {t("adminKpi.withdrawalsTitle")}
            </h3>
            <div className="inline-flex rounded-full border border-[#E5EBE7] p-1 bg-white">
              {["PENDING", "PAID", "REJECTED"].map((s) => (
                <button
                  key={s}
                  onClick={() => setWithdrawalStatus(s)}
                  className={`px-3 py-1 rounded-full text-xs ${withdrawalStatus === s ? "bg-[#16A34A] text-white" : "text-[#0A2518]"}`}
                  data-testid={`withdraw-tab-${s.toLowerCase()}`}
                >
                  {t(`adminKpi.tab_${s.toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>

          {withdrawals.length === 0 ? (
            <p className="text-sm text-[#6B7280]">{t("adminKpi.empty")}</p>
          ) : (
            <div className="space-y-3">
              {withdrawals.map((w) => (
                <div key={w.id} className="rounded-2xl border border-[#E5EBE7] p-4" data-testid={`withdraw-${w.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-medium text-[#0A2518]">{w.educator_name}</div>
                      <div className="text-xs text-[#6B7280]">{w.educator_email}</div>
                      <div className="text-xs text-[#6B7280] mt-1">{new Date(w.created_at).toLocaleString(isEn ? "en-US" : "fr-FR")}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl font-bold text-[#0A2518]">{fmt(w.amount_xof)}</div>
                      <div className="text-xs text-[#6B7280]">{t(`adminKpi.method_${w.method}`)}</div>
                      <div className="text-xs text-[#0A2518] font-mono break-all max-w-[200px]">{w.account_label}</div>
                    </div>
                    <StatusBadge status={w.status} />
                  </div>
                  {w.note && (
                    <div className="mt-2 text-xs text-[#4B5563]"><strong>{t("adminKpi.educatorNote")} :</strong> {w.note}</div>
                  )}
                  {w.admin_note && (
                    <div className="mt-2 text-xs text-[#4B5563]"><strong>{t("adminKpi.adminNote")} :</strong> {w.admin_note}</div>
                  )}
                  {w.status === "PENDING" && (
                    <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto_auto]">
                      <Textarea
                        rows={2}
                        placeholder={t("adminKpi.adminNotePh")}
                        value={decisionNote[w.id] || ""}
                        onChange={(e) => setDecisionNote((s) => ({ ...s, [w.id]: e.target.value }))}
                        className="text-sm"
                        data-testid={`withdraw-note-${w.id}`}
                      />
                      <Button onClick={() => decide(w.id, "PAID")} className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid={`withdraw-paid-${w.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> {t("adminKpi.markPaid")}
                      </Button>
                      <Button onClick={() => decide(w.id, "REJECTED")} variant="outline" className="rounded-full border-red-500 text-red-600 hover:bg-red-50" data-testid={`withdraw-reject-${w.id}`}>
                        <XCircle className="h-4 w-4 mr-1" /> {t("adminKpi.reject")}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, delta, icon: Icon, accent, testid }) {
  return (
    <Card className={`p-5 border-[#E5EBE7] ${accent ? "bg-[#F0FDF4]" : ""}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</div>
          <div className="mt-1 font-display text-2xl font-bold text-[#0A2518]">{value}</div>
          {sub && <div className="text-xs text-[#6B7280] mt-0.5">{sub}</div>}
        </div>
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent ? "bg-[#16A34A]/15" : "bg-[#F0FDF4]"} ring-1 ring-[#16A34A]/20`}>
          <Icon className="h-5 w-5 text-[#16A34A]" />
        </span>
      </div>
      {delta != null && (
        <DeltaBadge delta={delta} />
      )}
    </Card>
  );
}

function OpCard({ icon: Icon, label, value, sub, tone, testid }) {
  return (
    <Card className="p-4 border-[#E5EBE7]" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tone === "warn" ? "bg-amber-50 ring-amber-200" : "bg-[#F0FDF4] ring-[#16A34A]/15"} ring-1`}>
          <Icon className={`h-4 w-4 ${tone === "warn" ? "text-amber-600" : "text-[#16A34A]"}`} />
        </span>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</div>
          <div className="mt-0.5 font-display text-lg font-bold text-[#0A2518]">{value}</div>
          {sub && <div className="text-[11px] text-[#6B7280]">{sub}</div>}
        </div>
      </div>
    </Card>
  );
}

function DeltaBadge({ delta }) {
  if (delta == null) return null;
  const up = delta >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${up ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
      <Icon className="h-3 w-3" /> {up ? "+" : ""}{delta}%
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    PENDING: { cls: "bg-amber-100 text-amber-800", label: "Pending" },
    PAID: { cls: "bg-green-100 text-green-800", label: "Paid" },
    REJECTED: { cls: "bg-red-100 text-red-800", label: "Rejected" },
  };
  const v = map[status] || map.PENDING;
  return <Badge className={`${v.cls} border-0`} data-testid={`status-${status?.toLowerCase()}`}>{status}</Badge>;
}

function Table({ rows, cols, empty, testid }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-[#6B7280] py-2">{empty}</p>;
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-[#6B7280]">
          <tr className="border-b border-[#E5EBE7]">
            {cols.map((c) => (
              <th key={c.key} className={`py-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-[#F3F4F6] last:border-0 align-top">
              {cols.map((c) => (
                <td key={c.key} className={`py-2 ${c.align === "right" ? "text-right" : "text-left"}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
